
import { useState } from "react"
import {
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Container,
  Paper,
  FormControlLabel,
  Checkbox,
} from "@mui/material"
import { Send as SendIcon } from "@mui/icons-material"

export default function TaskGenerator() {
  const [formData, setFormData] = useState({
    industry: "",
    team: "",
    description: "",
    tools: "",
    restrictions: "",
    costEfficiency: false,
    urgentNeeds: false,
    freshIdeas: false,
    talentPipeline: false,
    corporateSocialResponsibility: false,
  })

  const [loading, setLoading] = useState(false)
  const [tasks, setTasks] = useState([])
  const [error, setError] = useState(false)

  const handleInputChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    })
  }

  const handleCheckboxChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.checked,
    })
  }

function getSelectedCheckboxesString(formData) {
  const options = {
    costEfficiency: "Cost-efficiency",
    urgentNeeds: "Handling urgent needs or workload peaks",
    freshIdeas: "Fresh ideas and outside perspective",
    talentPipeline: "Talent pipeline / trial for recruitment",
    corporateSocialResponsibility: "Corporate Social Responsibility",
  };

  return Object.entries(options)
    .filter(([key]) => formData[key])
    .map(([, label]) => label)
    .join(", ");
}


const handleSubmit = async () => {
  setLoading(true);
  setError(false);
  setTasks([]);

  try {
    // Combine the selected checkbox labels into a single string
    const checkboxesString = getSelectedCheckboxesString(formData);

    // Prepare the query data object with checkboxes as a string
    const queryData = {
      industry: formData.industry,
      team: formData.team,
      description: formData.description,
      tools: formData.tools,
      restrictions: formData.restrictions,
      checkboxes: checkboxesString,
    };

    const queryParams = new URLSearchParams(queryData).toString();

    const response = await fetch(`https://chadi.onrender.com/job-tasks?${queryParams}`);

    if (response.ok) {
      const data = await response.json();
      setTasks(data.tasks || []);
    } else {
      setError(true);
    }
  } catch (err) {
    setError(true);
  } finally {
    setLoading(false);
  }
};


  const isFormValid = formData.industry.trim() !== ""

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ fontFamily: "Oswald, sans-serif" }}>
          Task Generator
        </Typography>

        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Industry"
            variant="outlined"
            value={formData.industry}
            onChange={handleInputChange("industry")}
            placeholder="e.g., events, cleaning..."
            required
            sx={{ mb: 3, "& .MuiInputBase-input": { fontFamily: "Open Sans, sans-serif" } }}
          />

          <TextField
            fullWidth
            label="Department/role"
            variant="outlined"
            value={formData.team}
            onChange={handleInputChange("team")}
            placeholder="e.g., marketing"
            sx={{ mb: 3, "& .MuiInputBase-input": { fontFamily: "Open Sans, sans-serif" } }}
          />

          <TextField
            fullWidth
            label="Description"
            variant="outlined"
            multiline
            rows={3}
            value={formData.description}
            onChange={handleInputChange("description")}
            placeholder="Describe job work details"
            sx={{ mb: 3, "& .MuiInputBase-input": { fontFamily: "Open Sans, sans-serif" } }}
          />

          <TextField
            fullWidth
            label="Tools"
            variant="outlined"
            value={formData.tools}
            onChange={handleInputChange("tools")}
            placeholder="e.g., Excel, any tools you need for job"
            sx={{ mb: 3, "& .MuiInputBase-input": { fontFamily: "Open Sans, sans-serif" } }}
          />

          <TextField
            fullWidth
            label="Restrictions"
            variant="outlined"
            value={formData.restrictions}
            onChange={handleInputChange("restrictions")}
            placeholder="e.g., work safety"
            sx={{ mb: 3, "& .MuiInputBase-input": { fontFamily: "Open Sans, sans-serif" } }}
          />

          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              sx={{ mb: 2, fontFamily: "Open Sans, sans-serif", color: "#333", fontSize: "18px" }}
            >
              Why are you offering these tasks? (Select all that apply)
            </Typography>

            <Grid container spacing={2} sx={{ maxWidth: "100%" }}>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.costEfficiency}
                      onChange={handleCheckboxChange("costEfficiency")}
                      sx={{
                        color: "#ddd",
                        "&.Mui-checked": {
                          color: "#FF9900",
                        },
                        "& .MuiSvgIcon-root": {
                          fontSize: 20,
                        },
                      }}
                    />
                  }
                  label="Cost-efficiency"
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontFamily: "Open Sans, sans-serif",
                      fontSize: "14px",
                      lineHeight: 1.3,
                    },
                    width: "100%",
                    margin: 0,
                    alignItems: "center",
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.urgentNeeds}
                      onChange={handleCheckboxChange("urgentNeeds")}
                      sx={{
                        color: "#ddd",
                        "&.Mui-checked": {
                          color: "#FF9900",
                        },
                        "& .MuiSvgIcon-root": {
                          fontSize: 20,
                        },
                      }}
                    />
                  }
                  label="Handling urgent needs or workload peaks"
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontFamily: "Open Sans, sans-serif",
                      fontSize: "14px",
                      lineHeight: 1.3,
                    },
                    width: "100%",
                    margin: 0,
                    alignItems: "center",
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.freshIdeas}
                      onChange={handleCheckboxChange("freshIdeas")}
                      sx={{
                        color: "#ddd",
                        "&.Mui-checked": {
                          color: "#FF9900",
                        },
                        "& .MuiSvgIcon-root": {
                          fontSize: 20,
                        },
                      }}
                    />
                  }
                  label="Fresh ideas and outside perspective"
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontFamily: "Open Sans, sans-serif",
                      fontSize: "14px",
                      lineHeight: 1.3,
                    },
                    width: "100%",
                    margin: 0,
                    alignItems: "center",
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.talentPipeline}
                      onChange={handleCheckboxChange("talentPipeline")}
                      sx={{
                        color: "#ddd",
                        "&.Mui-checked": {
                          color: "#FF9900",
                        },
                        "& .MuiSvgIcon-root": {
                          fontSize: 20,
                        },
                      }}
                    />
                  }
                  label="Talent pipeline / trial for recruitment"
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontFamily: "Open Sans, sans-serif",
                      fontSize: "14px",
                      lineHeight: 1.3,
                    },
                    width: "100%",
                    margin: 0,
                    alignItems: "center",
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.corporateSocialResponsibility}
                      onChange={handleCheckboxChange("corporateSocialResponsibility")}
                      sx={{
                        color: "#ddd",
                        "&.Mui-checked": {
                          color: "#FF9900",
                        },
                        "& .MuiSvgIcon-root": {
                          fontSize: 20,
                        },
                      }}
                    />
                  }
                  label="Corporate Social Responsibility"
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontFamily: "Open Sans, sans-serif",
                      fontSize: "14px",
                      lineHeight: 1.3,
                    },
                    width: "100%",
                    margin: 0,
                    alignItems: "center",
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={!isFormValid || loading}
            startIcon={loading ? <CircularProgress size={20} sx={{ color: "#006699" }} /> : <SendIcon />}
            sx={{
              minWidth: 200,
              fontFamily: "Open Sans, sans-serif",
              textTransform: "uppercase",
              backgroundColor: "#006699",
              "&:hover": {
                backgroundColor: "#004d73",
              },
            }}
          >
            {loading ? "GENERATING TASKS..." : "GENERATE TASKS"}
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          <Typography variant="h6">Oops!</Typography>
          Something went wrong while generating tasks. Please try again.
        </Alert>
      )}

      {tasks.length > 0 && (
        <Box>
          <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 3 }}>
            Generated Tasks ({tasks.length})
          </Typography>

          <Grid container spacing={3}>
            {tasks.map((task, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Card
                  elevation={2}
                  sx={{
                    minHeight: 200,
                    width: "100%",
                    maxWidth: "350px",
                    display: "flex",
                    flexDirection: "column",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 4,
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column", p: 2 }}>
                    <Typography
                      variant="body1"
                      paragraph
                      sx={{
                        flexGrow: 1,
                        fontFamily: "Open Sans, sans-serif",
                        lineHeight: 1.5,
                        mb: 1,
                      }}
                    >
                      {task.task}
                    </Typography>

                    <Box sx={{ mt: "auto", pt: 1, borderTop: "1px solid #f0f0f0" }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                        sx={{ fontFamily: "Open Sans, sans-serif", mb: 1 }}
                      >
                        <strong>Duration:</strong> {task.estimatedDuration}
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                        sx={{ fontFamily: "Open Sans, sans-serif", mb: 1 }}
                      >
                        <strong>Suitable for:</strong> {task.suitability}
                      </Typography>

                      <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "Open Sans, sans-serif" }}>
                        <strong>Training:</strong> {task.requiresTraining}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Container>
  )
}
